import { CollapsibleCard, type ToolRendererProps } from '../ToolCard';

interface PrLike {
  number?: number;
  title?: string;
  state?: string;
  merged?: boolean;
  additions?: number;
  deletions?: number;
  user?: { login?: string };
  author?: { login?: string };
  html_url?: string;
}

interface CommitLike {
  sha?: string;
  commit?: { message?: string; author?: { name?: string } };
  message?: string;
  author?: { login?: string } | { name?: string };
  html_url?: string;
}

function statusPill(pr: PrLike): { label: string; cls: string } {
  if (pr.merged) return { label: 'merged', cls: 'bg-purple-100 text-purple-700' };
  if (pr.state === 'open') return { label: 'open', cls: 'bg-moss/20 text-moss' };
  if (pr.state === 'closed') return { label: 'closed', cls: 'bg-red-100 text-red-700' };
  return { label: pr.state ?? 'unknown', cls: 'bg-fog text-bark' };
}

export function GitHubCard({ toolName, result }: ToolRendererProps) {
  // PR detail — single object with {number, title, state, ...}
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    const pr = result as PrLike;
    if (pr.number !== undefined && pr.title !== undefined) {
      const pill = statusPill(pr);
      const author = pr.user?.login ?? pr.author?.login;
      return (
        <CollapsibleCard toolName={toolName} defaultOpen>
          <div className="flex items-start gap-2">
            <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${pill.cls}`}>
              {pill.label}
            </span>
            <div className="flex-1 text-xs text-espresso">
              <div className="font-medium">#{pr.number} {pr.title}</div>
              {author && <div className="mt-0.5 text-[10px] text-bark">by @{author}</div>}
              {(pr.additions !== undefined || pr.deletions !== undefined) && (
                <div className="mt-0.5 text-[10px] text-bark">
                  <span className="text-moss">+{pr.additions ?? 0}</span>
                  {' '}
                  <span className="text-red-600">-{pr.deletions ?? 0}</span>
                </div>
              )}
            </div>
          </div>
        </CollapsibleCard>
      );
    }
  }

  // Commit list — array of commits with sha + message
  if (Array.isArray(result)) {
    const commits = result as CommitLike[];
    return (
      <CollapsibleCard toolName={toolName} defaultOpen>
        <div className="text-[11px] text-bark">{commits.length} commits</div>
        <ul className="mt-1 space-y-1">
          {commits.slice(0, 10).map((c, i) => {
            const sha = (c.sha ?? '').slice(0, 7);
            const msg = (c.commit?.message ?? c.message ?? '').split('\n')[0];
            return (
              <li key={sha || i} className="flex gap-2 text-xs">
                <code className="shrink-0 font-mono text-[10px] text-bark">{sha}</code>
                <span className="flex-1 truncate text-espresso">{msg}</span>
              </li>
            );
          })}
        </ul>
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard toolName={toolName}>
      <pre className="text-[11px] text-espresso">{JSON.stringify(result, null, 2).slice(0, 500)}</pre>
    </CollapsibleCard>
  );
}
